import { ModalHeader } from '@elliemae/ds-modal-slide';
import {
  UseEligibleGridColumnProps,
  useLoanDataFieldSelector,
  useLoanDataFieldSelectorHook,
  useLookup,
  useLookupOptions,
  useOriginalLoanInformationSelector,
} from '@frontend/pricing-search';
import { get } from 'lodash';
import { arrayOf, bool, func, shape } from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  RequestActionType,
  RequestLockStatus,
} from '../../../../common/constants/enumTypes';
import { handleSelectProductDetails } from '../../../../common/services/sessionHelpers';
import { KPI, kpiStart } from '../../../../common/services/kpiTracking';
import { useAppData } from '../../../../common/utils/customHooks/useAppData';
import { selectProduct } from '../../../../data/lockSummary/actions';
import {
  useLoanInformationField,
  useSummaryDetailsField,
} from '../../../../data/useSelectors';
import { getSelectedProductSummaryPath } from '../../../../route/getSummaryPath';
import { getAdjustmentDetails } from '../../../AdjustmentDetails/AdjustmentDetailsAction';
import { ModalSlide } from '../../../components/ModalSlide';
import { GridPriceDetails } from '../GridPriceDetails/GridPriceDetails';
import { transformRateSelectorDataToSlide } from '../GridPriceDetails/helpers';
import { MIProvider } from '../GetMIButton';
import {
  getMICertification,
  isLockPeriodDisabled,
  toNumber,
} from '../../helpers';
import { getResultsColumns, hasEncompassApr } from './columns';
import { StyledAvailableProgramsHeading } from './styled';
import { EligibleProductsToolbar } from './EligibleProductsToolbar';
import { ResultsGrid } from './ResultsGrid';
import { filterPrograms } from './helpers';
import { useEligibleProductsGrid } from './useEligibleProductsGrid';

export const EligibleProductsGrid = ({
  rows,
  activeFilters,
  onFilterChange,
  isLoanPrograms,
  refreshPrograms,
  onSearch,
  updateAdjustmentDetailsCache,
}: any = {}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const setEligibleProducts = useAppData('setEligibleProducts');
  const clientsettings = useLookup('clientSettings', '');
  const isBuySideHistorical = get(
    clientsettings,
    '[0].options.isBuySideHistorical',
  );
  const shouldExpandBestPrice =
    String(
      get(clientsettings, '[0].options.expandBestPricing'),
    ).toLowerCase() !== 'false';
  // Coerced because the settings payload carries some options as strings. A
  // non numeric value would make the window bounds NaN and render an empty rate
  // stack, where 0 correctly means show every rate.
  const ratesToDisplay = toNumber(
    get(clientsettings, '[0].options.maxAlternates'),
  );
  const clientSettingsOptions = useLookupOptions('clientSettings');
  const loanTermsLookup = (useLookupOptions('loanTerms') as any[]) || [];
  const fullLoanData = useLoanDataFieldSelectorHook() as any;
  const originalLoanInformation = useOriginalLoanInformationSelector();
  const { relockRequest, contacts } = useSelector(
    ({ EPPS }) => EPPS.loanInfo || {},
  );
  const proposedMIAmount = useSelector(
    ({ EPPS }: any) =>
      EPPS.originalLoanInformation?.proposedMortgageInsuranceAmount,
  );
  const miCertification = getMICertification(contacts);
  const lienPosition = useLoanInformationField('lienPosition', 1);
  const productOptions = useLoanDataFieldSelector('productOptions', []) || [];
  const loanTerms = (useLoanDataFieldSelector('productType', []) ||
    []) as any[];
  const requestAction = useLoanDataFieldSelector('requestAction');
  const requestLockStatus = useLoanDataFieldSelector('requestLockStatus');
  const disableLockLinks =
    (requestAction === RequestActionType.ValidateReLock &&
      requestLockStatus === RequestLockStatus.ActiveLock) ||
    isLockPeriodDisabled(requestLockStatus);
  const { loanId } = useSelector(({ EPPS }) => EPPS.loanPrograms || {});
  const {
    lien: { armIndexValue = 0, arm = false } = {},
    loan: { mortgageType = '' } = {},
  } = useSummaryDetailsField() as any;

  function handleSelectProduct(
    programID,
    rateDataID,
    lockEffectiveDate,
    wcpPolicyApplied,
  ) {
    handleSelectProductDetails({
      loanId,
      programID,
      rateDataID,
      lockEffectiveDate,
      wcpPolicyApplied,
    });
    kpiStart(KPI.SelectRate);
    navigate(getSelectedProductSummaryPath());
  }
  const handleRefreshButton = (lock, loanData) => {
    // TODO(ngen): this was missing the dep array
    // This was also a useCallback - but I changed it to a regular callback function
    // Not sure how this was supposed to work
    const lockData = { lockDays: [Number(lock)] };
    const refreshLoanData = { ...loanData, ...lockData };
    onSearch(refreshLoanData, false);
  };

  const {
    handleSelectRow,
    slide,
    setSlide,
    filteredRows,
    isHistoricalPricing,
    store,
    handleGetMI,
  } = useEligibleProductsGrid({
    dispatch,
    loanId,
    relockRequest,
    lienPosition,
    productOptions,
    armIndexValue,
    mortgageType,
    transformRateSelectorDataToSlide,
    getAdjustmentDetails,
    activeFilters,
    filterPrograms,
    selectProduct,
    clientsettings,
    rows,
    refreshPrograms,
    arm,
    handleSelectProduct,
    handleSelectProductDetails,
    handleRefreshButton,
    updateAdjustmentDetailsCache,
    disableLockLinks,
    isBuySideHistorical,
    miCertification,
    proposedMIAmount,
  } as UseEligibleGridColumnProps);

  useEffect(() => {
    // TODO(ngen): this set command is causing an infinite loop
    // setEligibleProducts(filteredRows.length);
  }, [filteredRows]);

  const resultsColumns = useMemo(
    () =>
      getResultsColumns({
        clientSettings: clientSettingsOptions,
        ltv: fullLoanData?.loanInformation?.ltv
          ? parseFloat(fullLoanData.loanInformation.ltv)
          : originalLoanInformation?.ltv,
        standardProducts: fullLoanData?.standardProducts || [],
        loanTerms: fullLoanData?.productType || [],
        loanTermsLookup,
        loanChannel: fullLoanData?.loanInformation?.loanChannel,
        aprIsText: hasEncompassApr(rows),
      }),
    [
      clientSettingsOptions,
      fullLoanData?.loanInformation?.ltv,
      fullLoanData?.standardProducts,
      fullLoanData?.productType,
      fullLoanData?.loanInformation?.loanChannel,
      originalLoanInformation?.ltv,
      loanTermsLookup,
      rows,
    ],
  );

  const miContextValue = useMemo(
    () => ({ store, handleGetMI }),
    [store, handleGetMI],
  );

  if (clientsettings !== '') {
    return (
      <MIProvider value={miContextValue}>
        <StyledAvailableProgramsHeading>
          Available Programs
        </StyledAvailableProgramsHeading>
        <div
          style={{
            height: 'calc(100% - 60px)',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <EligibleProductsToolbar
            activeFilters={activeFilters}
            onFilterChange={onFilterChange}
            refreshPrograms={refreshPrograms}
            showTarget
            showFlagged
            showExceptionsApplied
            showGridSettings
            programs={rows}
          />
          <ModalSlide
            header={
              <ModalHeader
                title="Pricing Details"
                onClose={() =>
                  setSlide({
                    open: false,
                    data: null,
                    rowData: null,
                    rateData: null,
                  })
                }
              />
            }
            isOpen={slide.open}
          >
            {slide.data && (
              <div style={{ padding: 16 }}>
                <GridPriceDetails isSlide data={slide.data} />
              </div>
            )}
          </ModalSlide>
          <ResultsGrid
            programs={filteredRows}
            columns={resultsColumns}
            ratesToDisplay={ratesToDisplay}
            expandFirstProgram={shouldExpandBestPrice}
            relockRequest={relockRequest}
            rateRequestStatus={
              originalLoanInformation?.rateLock?.rateRequestStatus
            }
            isHistoricalPricing={isHistoricalPricing}
            disableLockLinks={disableLockLinks}
            loanData={fullLoanData}
            loanId={loanId}
            displayEstimatedMI={get(
              clientSettingsOptions,
              '[0].options.displayEstimatedMI',
            )}
            miCertification={miCertification}
            proposedMIAmount={proposedMIAmount}
            onRefreshLockDays={handleRefreshButton}
            onSelectRate={(program, rate) => {
              handleSelectRow({ ...program, rateDataID: rate.rateDataID });
              handleSelectProduct(
                program.programID,
                rate.rateDataID,
                rate.lockEffectiveDate,
                (program as any).wcpPolicyApplied,
              );
            }}
            onOpenDetails={(program, rate) => {
              handleSelectRow({ ...program, rateDataID: rate.rateDataID });
              handleSelectProductDetails({
                loanId,
                programID: program.programID,
                rateDataID: rate.rateDataID,
                lockEffectiveDate: rate.lockEffectiveDate,
                wcpPolicyApplied: (program as any).wcpPolicyApplied,
              });
              setSlide({
                rowData: program,
                rateData: rate.rateDataID,
                open: false,
                data: null,
              });
            }}
            onSelectProgram={handleSelectRow}
          />
        </div>
      </MIProvider>
    );
  }
  return null;
};

EligibleProductsGrid.defaultProps = {
  rows: [],
  activeFilters: [],
  onFilterChange: () => null,
  isLoanPrograms: false,
  refreshPrograms: false,
  onSearch: () => null,
  updateAdjustmentDetailsCache: () => null,
};

EligibleProductsGrid.propTypes = {
  rows: arrayOf(shape({})),
  activeFilters: arrayOf(shape({})),
  isLoanPrograms: arrayOf(shape({})),
  onFilterChange: func,
  refreshPrograms: bool,
  onSearch: func,
  updateAdjustmentDetailsCache: func,
};
